import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { CID } from '@/modules/shared/utils/store.types'
import { addItems, clearItems } from '@/modules/shared/utils/store'
import { useFetch as useFetchv4 } from '@/modules/core/composables/useFetch'
import { useNotificationStore } from '@/modules/shared/stores/notification-store'

///////////////////////////////////////////////////////////////////////////////
// Types
///////////////////////////////////////////////////////////////////////////////

type ID = number | string

type Template = {
  id: ID
  cc_roles: any[]
  message: string
  email_subject: string
  file_url: string
  provider_template_cache: any
  provider_template_id: string
  name: string
  display_name: string
  signing_roles: string[]
  templateable_id: string
  templateable_type: string
  title: string
  usage: number
  is_global: boolean
  usable_in_document_form: boolean
  dependency_template_ids: string[]
}

type TemplateMap = Map<CID, Template>

export const useTemplateStore = defineStore('template', () => {
  const route = useRoute()

  /////////////////////////////////////////////////////////////////////////////
  // STATE
  /////////////////////////////////////////////////////////////////////////////

  const editingSessionToken = ref(null)
  const new_template_uuid = ref(null)

  /////////////////////////////////////////////////////////////////////////////
  // COMPUTED
  /////////////////////////////////////////////////////////////////////////////

  const v4BaseUrl = computed(() => `/v4/${route.params.slug}/signing`)
  const template_items = ref<TemplateMap>(new Map())
  const templates = computed(() => Array.from(template_items.value.keys()).map((key) => template_items.value.get(key)))
  const notificationStore = useNotificationStore()

  const fetchTemplates = async (templateable_type, templateable_id) => {
    try {
      const url = `${v4BaseUrl.value}/templates?templateable_type=${templateable_type}&templateable_id=${templateable_id}`
      const result = await useFetchv4(url).get()

      clearItems(template_items)
      addItems(
        template_items,
        result.map((template) => ({ ...template, _cid: template.id })),
      )
    } catch (err: any) {
      console.error(err)
      notificationStore.enqueue('error', err.data || 'Failed to fetch templates')
    }
  }

  const removeTemplate = async (template_id) => {
    try {
      const url = `${v4BaseUrl.value}/templates/${template_id}`
      await useFetchv4(url).delete()
    } catch (err: any) {
      console.error(err)
      notificationStore.enqueue('error', err.data || 'Failed to remove template')
    }
  }

  const createTemplateEditingSession = async (provider_template_id: string) => {
    const url = `${v4BaseUrl.value}/templates/editing-session`

    const result = await useFetchv4(url).post({ provider_template_id })
    new_template_uuid.value = result['template_id']
    editingSessionToken.value = result['token']
  }

  const refreshTemplateCache = async (template_uuid: string) => {
    const url = `${v4BaseUrl.value}/templates/${template_uuid}/refresh-cache`

    await useFetchv4(url).post()
  }

  const createOnboardingDocument = async (spvId: string | number, providerTemplateId: string) => {
    const url = `${v4BaseUrl.value}/spvs/${spvId}/onboarding-documents`
    await useFetchv4(url).post({ provider_template_id: providerTemplateId })
    notificationStore.enqueue('success', 'Onboarding document created and sent for signing')
  }

  const uploadFile = async (payload: object, files: File[]) => {
    try {
      const url = `${v4BaseUrl.value}/templates`
      let body = payload
      if (files.length > 0) {
        body = new FormData()
        body.append('signing_template', JSON.stringify(payload.signing_template))
        files.forEach((file, index) => {
          body.append('files[]', file)
        })
      }

      const result = await useFetchv4(url).post(body)
      new_template_uuid.value = result['data']['template_id']
      editingSessionToken.value = result['data']['token']
    } catch (err: any) {
      notificationStore.enqueue('error', err.error || 'Failed to upload template')
    }
  }

  const updateTemplate = async (templateId: number | string, data: { name?: string; email_message?: string }) => {
    try {
      const url = `${v4BaseUrl.value}/templates/${templateId}/update-details`
      await useFetchv4(url).post(data)
      return true
    } catch (err: any) {
      console.error(err)
      notificationStore.enqueue('error', err.data || 'Failed to update template')
      return false
    }
  }

  /////////////////////////////////////////////////////////////////////////////
  // RETURN
  /////////////////////////////////////////////////////////////////////////////

  return {
    // state
    template_items,
    templates,
    editingSessionToken,
    new_template_uuid,
    // actions
    fetchTemplates,
    removeTemplate,
    createTemplateEditingSession,
    refreshTemplateCache,
    createOnboardingDocument,
    uploadFile,
    updateTemplate,
  }
})
